/**
 * COS-31 — EL CIERRE DEL AVISO "YA PUEDES CONTINUAR N PENDIENTES", POR EVENTO.
 *
 * LA FIRMA DE CARLOS, literal: cerrar el aviso lo mata para ESE evento, no para
 * siempre. Un "no volver a mostrar" permanente convertiría un aviso útil (los
 * créditos se resetearon, ya alcanza para continuar lo guardado) en algo que se
 * pierde para siempre por un tap; y un cierre puramente de sesión reaparecería
 * a la siguiente navegación, que es justo lo que molesta.
 *
 * QUÉ ES UN "EVENTO", entonces: el CONJUNTO de grupos pendientes que ahora mismo
 * se pueden continuar. Ese conjunto —`S`, los ids que el banner anunciaría— es
 * la identidad del aviso:
 *
 *   · Al cerrar se guarda `S` tal cual estaba.
 *   · El banner se muestra si `S` no está vacío Y (no hay cierre guardado O `S`
 *     trae algún id que NO estaba en el guardado). O sea: un grupo NUEVO que se
 *     vuelve continuable es un aviso nuevo y sí se ve, aunque el anterior se
 *     haya cerrado.
 *   · Cuando `S` se vacía (ya no hay nada que continuar) el cierre guardado se
 *     LIMPIA. Sin esa limpieza, el mismo grupo que vuelve a ser continuable tras
 *     un reset de créditos quedaría silenciado para siempre — el "para ESE
 *     evento" se habría vuelto "para siempre" por la puerta de atrás.
 *
 * POR APARATO, no por cuenta: es una preferencia de la instalación, igual que
 * `sb_signed_in_before` (lib/returningUser.js) y `lang_preference`
 * (lib/language.js). No viaja al servidor y no se compara contra ningún id de
 * usuario. Mismo try/catch de siempre: en modo privado o con el storage
 * bloqueado se degrada a "no hay cierre guardado" —el banner se ve, que es el
 * comportamiento de hoy— y jamás a un error.
 *
 * LO QUE NO TOCA: los badges de "More" y del renglón del sidebar. Salen de
 * `usePendingSummary().groupCount` y siguen apareciendo aunque el banner esté
 * cerrado; cerrar un aviso no es "ya no tengo pendientes".
 */
const PENDING_BANNER_DISMISSED_KEY = 'sb_pending_banner_dismissed';

/** El conjunto guardado, o null si no hay cierre vigente. */
export function readPendingBannerDismissal() {
  try {
    const raw = localStorage.getItem(PENDING_BANNER_DISMISSED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Una entrada corrupta (o de una versión futura) NO puede silenciar el
    // aviso: se trata como si no hubiera cierre.
    return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string') : null;
  } catch {
    return null;
  }
}

/** Persiste `S` (los ids continuables en el momento del cierre). */
export function savePendingBannerDismissal(ids) {
  try {
    localStorage.setItem(PENDING_BANNER_DISMISSED_KEY, JSON.stringify(Array.from(ids || [])));
  } catch { /* private mode: el aviso simplemente no se silencia */ }
}

export function clearPendingBannerDismissal() {
  try { localStorage.removeItem(PENDING_BANNER_DISMISSED_KEY); } catch { /* ídem */ }
}

/**
 * ¿El aviso de ESTE conjunto ya se cerró? Función PURA (la decisión de render
 * vive aquí, la persistencia arriba): silenciado sólo si hay cierre guardado y
 * todo lo continuable de ahora ya estaba dentro de él.
 */
export function pendingBannerIsSilenced(ids, dismissed) {
  if (!dismissed) return false;
  const cerrados = new Set(dismissed);
  return (ids || []).every(id => cerrados.has(id));
}

// Exportado para tests: fija el nombre único y constante de la llave.
export const _PENDING_BANNER_DISMISSED_KEY = PENDING_BANNER_DISMISSED_KEY;
